// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { JWTPayload, SignJWT, decodeJwt } from 'jose';

export const generateToken = async (
  userId: string,
  secret: string,
  audience: string,
  scope?: string,
): Promise<string> => {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    { name: 'HMAC', hash: { name: 'SHA-256' } },
    false,
    ['sign'],
  );

  return new SignJWT({ scope })
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()
    .setExpirationTime('24h')
    .setAudience(audience)
    .setSubject(userId)
    .sign(key);
};

export const decodeToken = (token: string): JWTPayload => {
  return decodeJwt(token);
};

// Exchange a username for a bearer token via the OAuth2 Resource Owner Password
// grant. Used by the compose local stack (mock-oauth2), where we keep the
// branded one-field login form but let the IdP mint a real RS-256 JWT the
// participant will accept.
export const fetchPasswordGrantToken = async (
  authority: string,
  clientId: string,
  username: string,
): Promise<string> => {
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: clientId,
    username,
    password: 'unused',
    scope: 'openid',
  });
  const res = await fetch(`${authority}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) {
    throw new Error(`Password-grant token request failed: ${res.status} ${res.statusText}`);
  }
  const json = (await res.json()) as { access_token?: string };
  if (!json.access_token) {
    throw new Error('Password-grant token response missing access_token');
  }
  return json.access_token;
};
